import { formConfigBase } from '@app/shared/constants/form-config-base';
import {
  CapitalStructureEventFaults,
  CapitalStructureInstrumentDefinitionDto,
  CapitalStructureRankInstrumentDto,
} from '@app/shared/models/contracts/capital-structure-debt-instrument-dto';
import {
  AdjustmentMode,
  BuildStructureErrorCodes,
  CouponType,
  EquityRefinancingMovementType,
  EventChangedType,
  EventKey,
  InstrumentType,
  OwnershipSplitType,
} from '@app/shared/models/contracts/enums/shared-enums';
import {
  DividendInstrumentDateDto,
  EquityRefinancingEventMovementDto,
  EquityRefinancingEventOwnerMovementDto,
  InstrumentCoupon,
  InstrumentDefinitionDto,
  InstrumentOwnershipDto,
  OwnershipDto,
} from '@app/shared/models/contracts/project-dto';
import * as capitalStructureActions from '@core/store/capital-structure-slice';
import { FC, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styles from './capital-structure-item-form-2.module.scss';
import useIsErfProject from '@app/shared/hooks/useIsErfProject';
import { useAppDispatch, useAppSelector } from '@app/core/hooks/redux-hooks';
import useIsReadOnly from '@app/core/hooks/customUseIsReadOnly';
import {
  selectBuildStructureResults,
  selectCapitalStructure,
  selectCapitalStructureId,
  selectCapitalStructures,
  selectInstrumentDefinitions,
  selectInstrumentsWithInitialValuesInitialInputs,
} from '@app/core/store/capital-structure-slice-selectors';
import {
  cloneToMutableData,
  enumKeyByValue,
  getProjectDraftWithRemovedInstrument,
  instrumentOwnersSortFn,
} from '@app/shared/helpers';
import { updateFormMovements } from '../tabs/build-structure-erf/utils/EventBuilderHelpers';
import { CapitalStructureItemFormFeatures } from './CapitalStructureItemFormFeatures';
import CapitalStructureEditTable from './components/existing-instrument-edit/CapitalStructureEditTable';
import { uiValuesSlice } from '@app/core/store/ui-values-slice';
import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { useLocale } from '@app/core/hooks/useLocale';
import Button from '@app/shared/components/button/Button';
import { ButtonAppearance, ButtonSize } from '@app/shared/components/button/button-enums';
import SvgTrash from '@app/shared/icons/Trash';
import { calculateBuildStructure } from '@core/store/capital-structure-slice';
import CapitalStructureCreateTable from './components/new-instrument-create/CapitalStructureCreateTable';
import useGetHistoricDividendYears from '@app/shared/hooks/useGetHistoricDividendYears';

export type EquityItem = Omit<CapitalStructureRankInstrumentDto, 'ownership'> &
  CapitalStructureInstrumentDefinitionDto & {
    instrumentId: string;
    ownership: OwnershipDto[];
    proRataAmount?: Nullable<number>;
    proRataShares?: Nullable<number>;
    proRataClosingAmount?: Nullable<number>;
    proRataClosingShares?: Nullable<number>;
    proRataAmountInputMode?: Nullable<AdjustmentMode>;
    proRataSharesInputMode?: Nullable<AdjustmentMode>;
    previousActiveCoupon?: Nullable<InstrumentCoupon>;
  };

interface CapitalStructureItemFormProps {
  equityItem: EquityItem;
  eventSetId: string;
  warningFaults?: CapitalStructureEventFaults[];
  onFormErrorsChange?: (hasErrors: boolean) => void;
}

const resolveRealBuybackAmount = (
  realBuybackAmount: Nullable<number> | undefined,
  valueDelta: Nullable<number> | undefined
): Nullable<number> => {
  const rba = realBuybackAmount ?? null;
  if ((rba ?? 0) >= 0 || (valueDelta ?? 0) >= 0) {
    return null;
  }
  return rba;
};

const CapitalStructureItemFormErf2: FC<CapitalStructureItemFormProps> = ({
  equityItem,
  eventSetId,
  warningFaults,
  onFormErrorsChange,
}): JSX.Element => {
  const isReadOnly = useIsReadOnly();
  const { l } = useLocale();
  const dispatch = useAppDispatch();
  const isErfProject = useIsErfProject();
  const storedActiveTabs = useAppSelector(
    (state) => state.uiValues.userSelections.capitalStructure.buildStructureOwnerTabsErf
  );
  const projectDraft = useAppSelector((state) => state.project.projectDraft);
  const historicDividendYears = useGetHistoricDividendYears();
  const buildStructureResults = useAppSelector(selectBuildStructureResults);
  const capitalStructure = useAppSelector(selectCapitalStructure);
  const capitalStructures = useAppSelector(selectCapitalStructures);
  const capitalStructureId = useAppSelector(selectCapitalStructureId);
  const activeSelectedInstrument = useAppSelector((state) => state.uiValues.activeInstrument);
  const initialValues = useAppSelector(selectInstrumentsWithInitialValuesInitialInputs);
  const instrumentDefinitions = useAppSelector(selectInstrumentDefinitions);
  const ordinaryEquityKey = enumKeyByValue(InstrumentType, InstrumentType.OrdinaryEquity);
  const selectedInstrumentResults = Object.entries(
    buildStructureResults?.eventSets?.[eventSetId]?.events.find(
      (e) => e.id === activeSelectedInstrument?.eventId
    )?.ranks?.[equityItem.rank]?.instruments ?? {}
  ).find(([instrumentId, _]) => instrumentId === activeSelectedInstrument?.instrumentId)?.[1];
  const projectOwnershipSplit =
    capitalStructure.events[activeSelectedInstrument?.eventId ?? '']?.movements[
      activeSelectedInstrument?.instrumentId ?? ''
    ]?.ownershipSplit ?? OwnershipSplitType.ProRata;

  const isEvent = !(activeSelectedInstrument?.eventId === EventKey.InitialCapitalStructure);
  const events = capitalStructure.events;
  const eventDate =
    events[activeSelectedInstrument?.eventId ?? EventKey.InitialCapitalStructure]?.eventDate ??
    projectDraft.investmentDate;
  const isOrdinaryEquity = equityItem.type === ordinaryEquityKey;
  const isInstrumentAddedOnThisEvent =
    (equityItem.changeFlags & EventChangedType.InstrumentAdded) ===
    EventChangedType.InstrumentAdded;

  const getOwnersBySequence = equityItem.ownership
    .slice()
    .sort((ownerA, ownerB) => instrumentOwnersSortFn(ownerA.owner, ownerB.owner));

  const focusFieldName = `instrumentNarrative`;

  const movementCoupon = isEvent
    ? capitalStructure.events?.[activeSelectedInstrument?.eventId ?? '']?.movements?.[
        activeSelectedInstrument?.instrumentId ?? ''
      ]?.coupon
    : capitalStructure.initialValues?.[activeSelectedInstrument?.instrumentId ?? '']?.coupon;

  const couponDateErrorCodes = [
    BuildStructureErrorCodes.CSB_MOVEMENT_COUPON_DATE_PREDATES_INVESTMENT,
    BuildStructureErrorCodes.CSB_INITIAL_COUPON_DATE_PREDATES_INVESTMENT,
  ];

  const relevantFaults = (warningFaults ?? []).filter(
    (fault) => fault.instrument === equityItem.instrumentId
  );

  const hasCouponDateError = relevantFaults.some((fault) =>
    couponDateErrorCodes.includes(fault.errorCode)
  );

  const formMethods = useForm<any>({
    ...formConfigBase,
    mode: 'onChange',
    defaultValues: {
      ...equityItem,
      ...(isOrdinaryEquity
        ? {}
        : {
            coupon: {
              ...movementCoupon,
              value: movementCoupon?.value != null ? movementCoupon.value * 100 : undefined,
              type:
                movementCoupon?.type ??
                (isInstrumentAddedOnThisEvent
                  ? enumKeyByValue(CouponType, CouponType.CompoundInterest)
                  : undefined),
              cashPayProportion:
                movementCoupon?.cashPayProportion != null
                  ? movementCoupon?.cashPayProportion * 100
                  : undefined,
            },
          }),
    },
  });

  const getInitialActiveTabs = () => {
    return equityItem.ownership
      .map(({ amount, currentAmount }, idx) =>
        Number(amount) > 0 || Number(currentAmount) > 0 ? idx : null
      )
      .filter((idx) => idx !== null) as number[];
  };

  const { trigger, reset, setFocus } = formMethods;

  const [isMounted, setIsMounted] = useState(false);
  const [currentEquityId, setCurrentEquityId] = useState<string>(equityItem.instrumentId);
  const [selectedSplitType, setSelectedSplitType] =
    useState<OwnershipSplitType>(projectOwnershipSplit);
  const [renderingSplitType, setRenderingSplitType] =
    useState<OwnershipSplitType>(projectOwnershipSplit);
  const [activeTabs, setActiveTabs] = useState<number[]>(
    storedActiveTabs[`${currentEquityId}-${activeSelectedInstrument?.eventId}`] ??
      getInitialActiveTabs()
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted) {
      formMethods.handleSubmit(formSubmitHandler, formSubmitHandler)();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSplitType]);

  useEffect(() => {
    if (currentEquityId !== equityItem.instrumentId) {
      setCurrentEquityId(equityItem.instrumentId);
      reset(
        {
          ...equityItem,
          coupon: {
            value: undefined,
            type: undefined,
            cashPayProportion: undefined,
          },
        },
        { keepDirtyValues: true }
      );
    }
    trigger();
  }, [currentEquityId, equityItem, formMethods, trigger]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const { errors } = formMethods.formState;
    if (onFormErrorsChange) {
      const hasErrors = Object.keys(errors).length > 0;
      onFormErrorsChange(hasErrors);
    }
  }, [formMethods.formState, onFormErrorsChange]);

  useEffect(() => {
    setFocus(focusFieldName);
  }, [formMethods]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const formValues = formMethods.getValues();
    updateFormMovements(formValues, selectedInstrumentResults, formMethods);
  }, [selectedInstrumentResults]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateInstrumentDefinitionsWithRankChange = (
    instrumentDefinitions: Record<string, InstrumentDefinitionDto>,
    equityItem: EquityItem,
    rankValue: number | undefined,
    ordinaryEquityKey: string
  ) => {
    const hasInstrumentWithSameRank = Object.entries(instrumentDefinitions).some(
      ([instrumentId, instrumentValues]) =>
        instrumentValues.rank === equityItem.rank && instrumentId !== equityItem.instrumentId
    );
    const isRankValueProvided = rankValue !== undefined;
    const isRankChangeIncrease = isRankValueProvided && rankValue > equityItem.rank;

    let updatedInstrumentDefinitionsRanks = cloneToMutableData(instrumentDefinitions);

    // if the instrument is on a rank of its own, it will join the instrument(s) of the next rank
    // if the instrument is joined by other instruments on the same rank, the below logic will ensure the instrument moves to a rank of its own
    if (hasInstrumentWithSameRank && isRankValueProvided && equityItem.type !== ordinaryEquityKey) {
      if (isRankChangeIncrease) {
        // increase the rank of all instruments with the same or higher rank, to allow the instrument to move to a rank of its own
        Object.entries(instrumentDefinitions).forEach(([instrumentId, instrumentValues]) => {
          if (instrumentValues.rank >= rankValue && instrumentId !== equityItem.instrumentId) {
            updatedInstrumentDefinitionsRanks = {
              ...updatedInstrumentDefinitionsRanks,
              [instrumentId]: {
                ...instrumentValues,
                rank: instrumentValues.rank + 1,
              },
            };
          }
        });
      } else {
        // increase the rank of all instruments with a higher rank, to allow the instrument to move to a rank of its own
        // note: ths instrument will not move to rankValue in this case, and will remain on it's current rank (whilst the other instruments move up)
        Object.entries(instrumentDefinitions).forEach(([instrumentId, instrumentValues]) => {
          if (instrumentValues.rank > rankValue && instrumentId !== equityItem.instrumentId) {
            updatedInstrumentDefinitionsRanks = {
              ...updatedInstrumentDefinitionsRanks,
              [instrumentId]: {
                ...instrumentValues,
                rank: instrumentValues.rank + 1,
              },
            };
          }
        });
      }
    }

    return {
      updatedInstrumentDefinitionsRanks,
      hasInstrumentWithSameRank,
      isRankChangeIncrease,
    };
  };

  const normalizeRanks = (instrumentDefinitions: Record<string, InstrumentDefinitionDto>) => {
    const instruments = Object.values(instrumentDefinitions);
    const sortedInstruments = instruments.sort((a, b) => a.rank - b.rank);

    // Creating a mapping of old ranks to new ranks
    let currentRank = 1;
    const rankMapping = new Map<number, number>();
    sortedInstruments.forEach((instrument) => {
      if (instrument.type !== ordinaryEquityKey) {
        if (!rankMapping.has(instrument.rank)) {
          rankMapping.set(instrument.rank, currentRank);
          currentRank++;
        }
      }
    });

    // Applying the new ranks to the instruments
    sortedInstruments.forEach((instrument) => {
      if (instrument.type !== ordinaryEquityKey) {
        instrument.rank = rankMapping.get(instrument.rank)!;
      }
    });

    // Creating the result object
    const sortedInstrumentDefinitions = sortedInstruments.reduce((acc, instrument) => {
      const key = Object.keys(instrumentDefinitions).find(
        (k) => instrumentDefinitions[k] === instrument
      );
      if (key) {
        acc[key] = instrument;
      }
      return acc;
    }, {} as Record<string, InstrumentDefinitionDto>);

    return sortedInstrumentDefinitions;
  };

  const updateDividendSets = (hasChangedToTrue: boolean) => {
    const instrumentId = equityItem.instrumentId;
    const updatedForecastDividendSets = cloneToMutableData(
      projectDraft.capitalStructures[capitalStructureId].forecastDividendSets
    );
    const updatedHistoricDividendInstruments = cloneToMutableData(
      projectDraft.capitalStructures[capitalStructureId].historicDividendInstruments
    );

    if (!hasChangedToTrue) {
      return {
        updatedForecastDividendSets,
        updatedHistoricDividendInstruments,
      };
    }

    Object.entries(updatedForecastDividendSets).forEach(([dividendSetId, dividendSet]) => {
      if (!Object.prototype.hasOwnProperty.call(dividendSet.instruments, instrumentId)) {
        const caseItem = projectDraft.pwermInput.cases.find(
          (caseItem) => caseItem.dividendSetId === dividendSetId
        );

        let forecastYears: string[] = [];

        if (caseItem) {
          forecastYears = caseItem.forecasts.map((forecast) => forecast.forecastYear);
        } else if (projectDraft.opmInput.selectedDividendSetId === dividendSetId) {
          forecastYears = projectDraft.opmInput.perYearInputs.map(
            (perYearInput) => perYearInput.forecastDate
          );
        }
        const dates: Record<string, DividendInstrumentDateDto> = {};
        forecastYears.forEach((year) => {
          dates[year] = { totalDividendValue: null };
        });
        dividendSet.instruments[instrumentId] = {
          instrumentId,
          dates,
        };
      }
    });

    if (!Object.prototype.hasOwnProperty.call(updatedHistoricDividendInstruments, instrumentId)) {
      const dates: Record<string, DividendInstrumentDateDto> = {};
      historicDividendYears.forEach((year) => {
        dates[year] = { totalDividendValue: null };
      });
      updatedHistoricDividendInstruments[instrumentId] = {
        instrumentId,
        dates,
      };
    }

    return {
      updatedForecastDividendSets,
      updatedHistoricDividendInstruments,
    };
  };

  const removeInstrument = async (instrumentId: string | undefined) => {
    if (instrumentId) {
      await dispatch(
        calculateBuildStructure({
          project: getProjectDraftWithRemovedInstrument({ draft: projectDraft, instrumentId }),
          ignoreErrors: true,
        })
      );

      dispatch(
        uiValuesSlice.actions.setActiveInstrument({
          instrumentId: undefined,
        })
      );
    }
  };

  const formSubmitHandler = async () => {
    if (!isReadOnly) {
      await submitForm();
    }
  };

  const submitForm = async (rankValue?: number) => {
    isEvent ? submitEventForm(rankValue) : submitOriginalStructureForm(rankValue);
  };

  const submitEventForm = async (rankValue?: number) => {
    if (!activeSelectedInstrument?.eventId || !activeSelectedInstrument?.instrumentId) {
      return null;
    }

    const movements = cloneToMutableData({
      ...capitalStructure.events[activeSelectedInstrument?.eventId].movements,
    }) as Record<string, EquityRefinancingEventMovementDto>;

    // Removing any previous movements in the instrument in case the user wants to cancel them
    // Except when the instrument is added on this event, otherwise it would just vanish from the page.
    if (
      !isInstrumentAddedOnThisEvent &&
      Object.prototype.hasOwnProperty.call(movements, activeSelectedInstrument.instrumentId)
    ) {
      delete movements[activeSelectedInstrument.instrumentId];
    }

    if (isInstrumentAddedOnThisEvent) {
      movements[activeSelectedInstrument.instrumentId].ownerMovements = {};
    }

    const formValues = formMethods.getValues();
    const ownershipValues = formValues.ownership;

    const ownershipMovements = ownershipValues.map((o: EquityRefinancingEventOwnerMovementDto) => {
      const ownerFormValue = formValues.ownership.find(
        (formOwner: EquityRefinancingEventOwnerMovementDto) => formOwner.owner === o.owner
      );

      return {
        ...o,
        // Mode-driven cleanup for amounts
        amount: ownerFormValue?.amountInputMode === AdjustmentMode.Absolute ? null : o.amount,
        closingAmount:
          ownerFormValue?.amountInputMode === AdjustmentMode.Relative ? null : o.closingAmount,

        // Mode-driven cleanup for shares
        numberOfShares:
          ownerFormValue?.numberOfSharesInputMode === AdjustmentMode.Absolute
            ? null
            : o.numberOfShares,
        closingNumberOfShares:
          ownerFormValue?.numberOfSharesInputMode === AdjustmentMode.Relative
            ? null
            : o.closingNumberOfShares,
        realBuybackAmount: resolveRealBuybackAmount(
          ownerFormValue?.realBuybackAmount,
          equityItem.valueDeltas[o.owner]
        ),
      };
    });

    const updatedMovements = ownershipMovements.filter(
      (o: EquityRefinancingEventOwnerMovementDto) =>
        o.amount !== null ||
        o.numberOfShares !== null ||
        o.closingAmount !== null ||
        o.closingNumberOfShares !== null
    );

    const couponValue =
      !hasCouponDateError && formValues.coupon.value != null && formValues.coupon.value !== ''
        ? formValues.coupon.value
        : undefined;

    const couponStartDate = isInstrumentAddedOnThisEvent ? formValues.coupon.date : eventDate;

    const couponType = !hasCouponDateError ? formValues.coupon.type : undefined;

    const cashPayProportion =
      !hasCouponDateError &&
      formValues.coupon.cashPayProportion != null &&
      formValues.coupon.cashPayProportion !== ''
        ? formValues.coupon.cashPayProportion / 100
        : undefined;

    const proRataMovement = {
      amount:
        formValues.proRataAmountInputMode === AdjustmentMode.Absolute
          ? null
          : formValues.proRataAmount,
      numberOfShares:
        formValues.proRataSharesInputMode === AdjustmentMode.Absolute
          ? null
          : formValues.proRataShares,
      closingAmount:
        formValues.proRataAmountInputMode === AdjustmentMode.Relative
          ? null
          : formValues.proRataClosingAmount,
      closingNumberOfShares:
        formValues.proRataSharesInputMode === AdjustmentMode.Relative
          ? null
          : formValues.proRataClosingShares,
      realBuybackAmount: resolveRealBuybackAmount(
        formValues.proRataRealBuybackAmount,
        equityItem.totalValueDelta
      ),
    };
    // This logic is necessary to block sending unnecessary pro-rata objects to the backend,
    // or pro-rata ownershipSplit if they are just switched and do not contain meaningful values.
    const isProRataMovementInvalid = Object.values(proRataMovement).every(
      (value) => value === null
    );

    let updatedInstrumentMovement = movements;

    if (!isInstrumentAddedOnThisEvent) {
      updatedInstrumentMovement = {
        ...movements,
        [activeSelectedInstrument.instrumentId]: {
          ...movements[activeSelectedInstrument.instrumentId],
          ownerMovements: {},
          ownershipSplit: isProRataMovementInvalid ? OwnershipSplitType.Manual : selectedSplitType,
          movementType: EquityRefinancingMovementType.ValueAdjustment,
        },
      };
    }

    if (
      updatedMovements.length > 0 ||
      (couponValue !== null && couponValue !== undefined) ||
      (couponType !== null && couponType !== undefined) ||
      (cashPayProportion !== null && cashPayProportion !== undefined)
    ) {
      const ownerMovements = updatedMovements.reduce(
        (
          acc: Record<string, InstrumentOwnershipDto>,
          ownership: EquityRefinancingEventOwnerMovementDto
        ) => {
          const ownerFormValue = formValues.ownership.find(
            (o: EquityRefinancingEventOwnerMovementDto) => o.owner === ownership.owner
          );
          return {
            ...acc,
            [ownership.owner]: {
              amount:
                ownerFormValue?.amountInputMode === AdjustmentMode.Absolute
                  ? null
                  : ownership.amount,
              numberOfShares:
                ownerFormValue?.numberOfSharesInputMode === AdjustmentMode.Absolute
                  ? null
                  : ownership.numberOfShares,
              closingAmount:
                ownerFormValue?.amountInputMode === AdjustmentMode.Relative
                  ? null
                  : ownership.closingAmount,
              closingNumberOfShares:
                ownerFormValue?.numberOfSharesInputMode === AdjustmentMode.Relative
                  ? null
                  : ownership.closingNumberOfShares,
              realBuybackAmount: resolveRealBuybackAmount(
                ownerFormValue?.realBuybackAmount,
                equityItem.valueDeltas[ownership.owner]
              ),
            },
          };
        },
        {}
      );
      updatedInstrumentMovement = {
        ...movements,
        [activeSelectedInstrument.instrumentId]: {
          ...(movements[activeSelectedInstrument.instrumentId] ?? {}),
          coupon:
            instrumentDefinitions[equityItem.instrumentId].type !== ordinaryEquityKey &&
            ((couponValue !== null && couponValue !== undefined) ||
              (couponType !== null && couponType !== undefined) ||
              (cashPayProportion !== null && cashPayProportion !== undefined))
              ? {
                  value: couponValue != null ? couponValue / 100 : null,
                  date: couponStartDate,
                  type: couponType,
                  cashPayProportion: cashPayProportion ?? null,
                }
              : undefined,
          movementType: isInstrumentAddedOnThisEvent
            ? EquityRefinancingMovementType.NewInstrument
            : EquityRefinancingMovementType.ValueAdjustment,
          ownerMovements,
          ownershipSplit:
            isInstrumentAddedOnThisEvent || isProRataMovementInvalid
              ? OwnershipSplitType.Manual
              : selectedSplitType,
        },
      };
      const instrumentMovement = updatedInstrumentMovement[activeSelectedInstrument.instrumentId];
      if (instrumentMovement) {
        if (selectedSplitType === OwnershipSplitType.ProRata) {
          instrumentMovement.ownerMovements = {};
        } else if (selectedSplitType === OwnershipSplitType.Manual) {
          instrumentMovement.proRataMovement = undefined;
        }
      }
    }

    if (
      formValues.proRataAmount !== null ||
      formValues.proRataShares !== null ||
      formValues.proRataClosingAmount !== null ||
      formValues.proRataClosingShares !== null
    ) {
      const movement =
        updatedInstrumentMovement[activeSelectedInstrument.instrumentId]?.ownerMovements ?? {};
      updatedInstrumentMovement = {
        ...updatedInstrumentMovement,
        [activeSelectedInstrument.instrumentId]: {
          ...updatedInstrumentMovement[activeSelectedInstrument.instrumentId],
          proRataMovement: proRataMovement,
          movementType: isInstrumentAddedOnThisEvent
            ? EquityRefinancingMovementType.NewInstrument
            : EquityRefinancingMovementType.ValueAdjustment,
          ownerMovements: movement,
          ownershipSplit: isProRataMovementInvalid ? OwnershipSplitType.Manual : selectedSplitType,
        },
      };
      if (isProRataMovementInvalid) {
        delete updatedInstrumentMovement[activeSelectedInstrument.instrumentId].proRataMovement;
      }
    }

    const { updatedInstrumentDefinitionsRanks, hasInstrumentWithSameRank, isRankChangeIncrease } =
      updateInstrumentDefinitionsWithRankChange(
        instrumentDefinitions,
        equityItem,
        rankValue,
        ordinaryEquityKey
      );

    const { updatedForecastDividendSets, updatedHistoricDividendInstruments } = updateDividendSets(
      formValues.dividendEligible && !equityItem.dividendEligible
    );

    const updatedProjectDraft = {
      ...projectDraft,
      capitalStructures: {
        ...projectDraft.capitalStructures,
        [capitalStructureId]: {
          ...projectDraft.capitalStructures[capitalStructureId],
          events: {
            ...projectDraft.capitalStructures[capitalStructureId].events,
            [activeSelectedInstrument?.eventId]: {
              ...projectDraft.capitalStructures[capitalStructureId].events[
                activeSelectedInstrument?.eventId
              ],
              movements: updatedInstrumentMovement,
            },
          },
          instrumentDefinitions: {
            ...updatedInstrumentDefinitionsRanks,
            [activeSelectedInstrument?.instrumentId]: {
              ...projectDraft.capitalStructures[capitalStructureId].instrumentDefinitions[
                activeSelectedInstrument.instrumentId
              ],
              isSweetEquity: formMethods.getValues().isSweetEquity ?? equityItem.isSweetEquity,
              dividendEligible:
                formMethods.getValues().dividendEligible ?? equityItem.dividendEligible,
              rank:
                hasInstrumentWithSameRank && !isRankChangeIncrease
                  ? equityItem.rank
                  : rankValue ?? equityItem.rank,
              instrumentNarrative: formMethods.getValues().instrumentNarrative,
              shouldBeValued: formMethods.getValues().shouldBeValued,
            },
          },
          forecastDividendSets: updatedForecastDividendSets,
          historicDividendInstruments: updatedHistoricDividendInstruments,
        },
      },
    };

    await dispatch(
      capitalStructureActions.calculateBuildStructure({
        project: updatedProjectDraft,
        ignoreErrors: true,
      })
    );
    setRenderingSplitType(selectedSplitType);
  };

  const submitOriginalStructureForm = async (rankValue?: number) => {
    const formValues = formMethods.getValues();
    const couponValue = formValues.coupon?.value ?? 0;
    const couponStartDate = formValues.coupon?.date ?? projectDraft.investmentDate;
    const updatedInitialValues = {
      ...initialValues,
      [equityItem.instrumentId]: {
        ...initialValues[equityItem.instrumentId],
        coupon:
          instrumentDefinitions[equityItem.instrumentId].type !== ordinaryEquityKey
            ? {
                value: couponValue / 100,
                date: couponStartDate,
                type: formValues.coupon?.type,
                cashPayProportion: formValues.coupon?.cashPayProportion / 100,
              }
            : null,
        ownership: formMethods
          .getValues()
          .ownership.reduce(
            (acc: Record<string, InstrumentOwnershipDto>, ownership: OwnershipDto) => {
              if (ownership.amount !== null || ownership.numberOfShares !== null) {
                return {
                  ...acc,
                  [ownership.owner]: {
                    amount: ownership.amount,
                    numberOfShares: ownership.numberOfShares,
                  },
                };
              }
              return acc;
            },
            {}
          ),
      },
    };

    const { updatedInstrumentDefinitionsRanks, hasInstrumentWithSameRank, isRankChangeIncrease } =
      updateInstrumentDefinitionsWithRankChange(
        instrumentDefinitions,
        equityItem,
        rankValue,
        ordinaryEquityKey
      );

    const updatedInstrumentDefinitions = {
      ...updatedInstrumentDefinitionsRanks,
      [equityItem.instrumentId]: {
        ...instrumentDefinitions[equityItem.instrumentId],
        instrumentNarrative: formValues.instrumentNarrative,
        shouldBeValued: formValues.shouldBeValued,
        isSweetEquity: formValues.isSweetEquity,
        dividendEligible: formValues.dividendEligible,
        rank:
          hasInstrumentWithSameRank && !isRankChangeIncrease
            ? equityItem.rank
            : rankValue ?? equityItem.rank,
      },
    };

    const { updatedForecastDividendSets, updatedHistoricDividendInstruments } = updateDividendSets(
      formValues.dividendEligible && !equityItem.dividendEligible
    );

    const updatedProjectDraft = {
      ...projectDraft,
      capitalStructures: {
        ...capitalStructures,
        [capitalStructureId]: {
          ...capitalStructures[capitalStructureId],
          initialValues: updatedInitialValues,
          instrumentDefinitions: updatedInstrumentDefinitions,
          forecastDividendSets: updatedForecastDividendSets,
          historicDividendInstruments: updatedHistoricDividendInstruments,
        },
      },
    };

    const updatedProjectDraftWithNormalizedRanks = {
      ...updatedProjectDraft,
      capitalStructures: {
        ...updatedProjectDraft.capitalStructures,
        [capitalStructureId]: {
          ...updatedProjectDraft.capitalStructures[capitalStructureId],
          instrumentDefinitions: normalizeRanks(cloneToMutableData(updatedInstrumentDefinitions)),
        },
      },
    };

    await dispatch(
      capitalStructureActions.calculateBuildStructure({
        project: updatedProjectDraftWithNormalizedRanks,
      })
    );
  };

  return (
    <>
      <FormProvider {...formMethods}>
        <form
          data-cy="capital-structure-form"
          className={isErfProject ? styles['capital-structure-form__modal'] : ''}
          onBlur={(event) => {
            // Check if the blurred element is not a radio button to avoid double submission
            if (event.target.type !== 'radio') {
              formMethods.handleSubmit(formSubmitHandler, formSubmitHandler)();
            }
          }}>
          <CapitalStructureItemFormFeatures
            equityItem={equityItem}
            eventSetId={eventSetId}
            isEvent={isEvent}
            isBeforeOrOnValuationDate={eventDate <= projectDraft.valuationDate}
            submitForm={submitForm}
            hasCouponDateError={hasCouponDateError}
          />
          {!isInstrumentAddedOnThisEvent ? (
            <>
              <div>
                <RadioGroup row key="expandType" name="expandType" value={selectedSplitType}>
                  <div className={styles['input-radio-section']}>
                    <FormControlLabel
                      value={OwnershipSplitType.ProRata}
                      control={<Radio />}
                      label={l('_ByAggregate')}
                      onChange={() => setSelectedSplitType(OwnershipSplitType.ProRata)}
                    />
                    <FormControlLabel
                      value={OwnershipSplitType.Manual}
                      control={<Radio />}
                      label={l('_ByOwner')}
                      onChange={() => setSelectedSplitType(OwnershipSplitType.Manual)}
                    />
                  </div>
                </RadioGroup>
              </div>
              <CapitalStructureEditTable
                equityItem={equityItem}
                section={OwnershipSplitType.ProRata}
                renderingSplitType={renderingSplitType}
                activeTabs={activeTabs}
                setActiveTabs={setActiveTabs}
                currentEquityId={currentEquityId}
                eventId={activeSelectedInstrument?.eventId}
                isOrdinaryEquity={isOrdinaryEquity}
              />
              {getOwnersBySequence.map((item) => (
                <CapitalStructureEditTable
                  equityItem={equityItem}
                  section={item.owner}
                  renderingSplitType={renderingSplitType}
                  key={item.owner}
                  activeTabs={activeTabs}
                  setActiveTabs={setActiveTabs}
                  currentEquityId={currentEquityId}
                  eventId={activeSelectedInstrument?.eventId}
                  isOrdinaryEquity={isOrdinaryEquity}
                />
              ))}
            </>
          ) : (
            <CapitalStructureCreateTable equityItem={equityItem} />
          )}
          {isErfProject && (
            <Button
              onClick={() => removeInstrument(activeSelectedInstrument?.instrumentId)}
              size={ButtonSize.FULL_WIDTH}
              startIcon={<SvgTrash />}
              appearance={ButtonAppearance.DEFAULT_SECONDARY}
              disabled={!isInstrumentAddedOnThisEvent}>
              {l('_DeleteInstrument')}
            </Button>
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default CapitalStructureItemFormErf2;
